import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import type { ElementType } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { useModalStyles } from '../modal.styles';
import type { ModalAction, ModalFooterConfig } from '../modal.types';

const renderIcon = (icon?: ElementType) => (icon ? <Icon icon={icon} size="sm" /> : undefined);

type FooterActionButtonProps = {
  action: ModalAction;
  variant: 'contained' | 'text';
  isDisabled: boolean;
};

const FooterActionButton = ({ action, variant, isDisabled }: FooterActionButtonProps) => {
  const { translate } = useTranslation();
  const tooltipContent = action.tooltipMessage
    ? translate(action.tooltipMessage as LangEnum)
    : undefined;

  const nonDestructiveColor = variant === 'text' ? 'secondary' : 'primary';
  const buttonColor = action.variant === 'destructive' ? 'error' : nonDestructiveColor;

  return (
    <Tooltip TooltipContent={tooltipContent} tabIndex={isDisabled || action.isLoading ? 0 : -1}>
      <Button
        variant={variant}
        color={buttonColor}
        href={action.href}
        startIcon={renderIcon(action.startIcon)}
        endIcon={renderIcon(action.endIcon)}
        type={action.type}
        form={action.form}
        onClick={action.onClick}
        disabled={isDisabled}
        loading={action.isLoading}
        testId={action.testId}
      >
        {translate(action.label as LangEnum)}
      </Button>
    </Tooltip>
  );
};

const ModalFooter = ({ primaryAction, secondaryAction, tertiaryAction }: ModalFooterConfig) => {
  const { classes } = useModalStyles();
  const { translate } = useTranslation();
  const isPending = Boolean(primaryAction?.isLoading);

  return (
    <Box className={classes.footer}>
      {tertiaryAction &&
        (tertiaryAction.onClick ? (
          <Button
            variant="text"
            color="secondary"
            size="small"
            className={classes.footerTertiary}
            onClick={tertiaryAction.onClick}
            disabled={isPending}
            testId={tertiaryAction.testId}
          >
            {translate(tertiaryAction.label as LangEnum)}
          </Button>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            className={classes.footerTertiary}
            testId={tertiaryAction.testId}
          >
            {translate(tertiaryAction.label as LangEnum)}
          </Typography>
        ))}
      <Box className={classes.footerActions}>
        {secondaryAction && (
          <FooterActionButton
            action={secondaryAction}
            variant="text"
            isDisabled={Boolean(secondaryAction.isDisabled) || isPending}
          />
        )}
        {primaryAction && (
          <FooterActionButton
            action={primaryAction}
            variant="contained"
            isDisabled={Boolean(primaryAction.isDisabled)}
          />
        )}
      </Box>
    </Box>
  );
};

export default ModalFooter;
